import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { formatDate } from '../../../../utils/helper';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { MatenFilter, MatenTable, MatenTableAction, MatenTableColumn } from '../../../../shared';
import RequestOverviewStore from '../stores/RequestOverviewStore';
import { RequestStatus } from '../../ShareComponents';
import RequestDialogStore from '../stores/RequestDialogStore';

// Dialog components
const DeleteConfirmationDialog = observer(
    ({
        store,
        onConfirm,
        onCancel,
    }: {
        store: any;
        onConfirm: (reason: string) => void;
        onCancel: () => void;
    }) => {
        const [confirmationText, setConfirmationText] = useState('');
        const [reason, setReason] = useState('');
        const searchWord: string = 'löschen';

        const handleConfirm = () => {
            if (confirmationText.toLowerCase() === searchWord && reason.trim()) {
                setConfirmationText('');
                setReason('');
                onConfirm(reason);
            }
        };

        const handleCancel = () => {
            setConfirmationText('');
            setReason('');
            onCancel();
        };

        const requestName: string = `Antrag von ${store?.requestDeleteConfirmation?.user_name || 'Unbekannt'}`;

        return (
            <Dialog
                visible={!!store.requestDeleteConfirmation}
                modal
                header={'Antrag löschen bestätigen'}
                footer={() => (
                    <div>
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={handleCancel}
                        />
                        <Button
                            label="Löschen"
                            icon="pi pi-trash"
                            className="p-button-danger"
                            onClick={handleConfirm}
                            disabled={
                                confirmationText.toLowerCase() !== searchWord || !reason.trim()
                            }
                        />
                    </div>
                )}
                onHide={handleCancel}
                style={{ width: '32rem' }}
            >
                <div className="confirmation-content">
                    <p>
                        Möchten Sie den Antrag <strong>{requestName}</strong> wirklich löschen?
                    </p>
                    <div className="field">
                        <label htmlFor="reason">Grund für die Löschung:</label>
                        <InputTextarea
                            id="reason"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="Grund eingeben..."
                            rows={3}
                            style={{ width: '100%', marginTop: '0.5rem' }}
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="confirmation">
                            Geben Sie <strong>"{searchWord}"</strong> ein, um zu bestätigen:
                        </label>
                        <InputText
                            id="confirmation"
                            value={confirmationText}
                            onChange={e => setConfirmationText(e.target.value)}
                            placeholder={searchWord}
                            style={{ width: '100%', marginTop: '0.5rem' }}
                        />
                    </div>
                </div>
            </Dialog>
        );
    }
);

const ApproveConfirmationDialog = observer(
    ({
        store,
        onConfirm,
        onCancel,
    }: {
        store: any;
        onConfirm: (comment: string) => void;
        onCancel: () => void;
    }) => {
        const [comment, setComment] = useState('');

        const handleConfirm = () => {
            onConfirm(comment);
            setComment('');
        };

        const handleCancel = () => {
            setComment('');
            onCancel();
        };

        const requestName: string = `Antrag von ${store?.requestApproveConfirmation?.user_name || 'Unbekannt'}`;

        return (
            <Dialog
                visible={!!store.requestApproveConfirmation}
                modal
                header={'Antrag genehmigen'}
                footer={() => (
                    <div>
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={handleCancel}
                        />
                        <Button
                            label="Genehmigen"
                            icon="pi pi-check"
                            className="p-button-success"
                            onClick={handleConfirm}
                        />
                    </div>
                )}
                onHide={handleCancel}
                style={{ width: '32rem' }}
            >
                <div className="confirmation-content">
                    <p>
                        Möchten Sie den Antrag <strong>{requestName}</strong> genehmigen?
                    </p>
                    <div className="field">
                        <label htmlFor="comment">Kommentar (optional):</label>
                        <InputTextarea
                            id="comment"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            placeholder="Kommentar zur Genehmigung..."
                            rows={3}
                            style={{ width: '100%', marginTop: '0.5rem' }}
                        />
                    </div>
                </div>
            </Dialog>
        );
    }
);

const RejectConfirmationDialog = observer(
    ({
        store,
        onConfirm,
        onCancel,
    }: {
        store: any;
        onConfirm: (reason: string) => void;
        onCancel: () => void;
    }) => {
        const [reason, setReason] = useState('');

        const handleConfirm = () => {
            if (reason.trim()) {
                onConfirm(reason);
                setReason('');
            }
        };

        const handleCancel = () => {
            setReason('');
            onCancel();
        };

        const requestName: string = `Antrag von ${store?.requestRejectConfirmation?.user_name || 'Unbekannt'}`;

        return (
            <Dialog
                visible={!!store.requestRejectConfirmation}
                modal
                header={'Antrag ablehnen'}
                footer={() => (
                    <div>
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={handleCancel}
                        />
                        <Button
                            label="Ablehnen"
                            icon="pi pi-times"
                            className="p-button-danger"
                            onClick={handleConfirm}
                            disabled={!reason.trim()}
                        />
                    </div>
                )}
                onHide={handleCancel}
                style={{ width: '32rem' }}
            >
                <div className="confirmation-content">
                    <p>
                        Möchten Sie den Antrag <strong>{requestName}</strong> ablehnen?
                    </p>
                    <div className="field">
                        <label htmlFor="reason">Grund für die Ablehnung:</label>
                        <InputTextarea
                            id="reason"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="Grund für die Ablehnung..."
                            rows={3}
                            style={{ width: '100%', marginTop: '0.5rem' }}
                        />
                    </div>
                </div>
            </Dialog>
        );
    }
);

const RequestOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(RequestOverviewStore);
    }, [sdk]);

    // Define columns for requests
    const columns: MatenTableColumn[] = [
        {
            field: 'status',
            header: 'Status',
            sortable: false,
            style: { textAlign: 'center' },
            body: (rowData: any) => <RequestStatus status={rowData.status} />,
        },
        {
            field: 'id',
            header: 'Antrags-Id',
            sortable: true,
        },
        {
            field: 'employee_name',
            header: 'Antragssteller',
            sortable: false,
        },
        {
            field: 'request_type.name',
            header: 'Typ',
            sortable: false,
        },
        {
            field: 'start_date',
            header: 'Startdatum',
            sortable: true,
            body: (rowData: any) => {
                const date = new Date(rowData.start_date);
                return date.toLocaleDateString('de-DE');
            },
        },
        {
            field: 'end_date',
            header: 'Enddatum',
            sortable: true,
            body: (rowData: any) => {
                const date = new Date(rowData.end_date);
                return date.toLocaleDateString('de-DE');
            },
        },
        {
            field: 'created',
            header: 'Erstellt',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.created_utc),
        },
    ];

    // Additional header content
    const headerContent = (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
                label={'Neu'}
                icon="pi pi-plus"
                aria-label="Filter"
                onClick={() => sdk.openDialog(RequestDialogStore, { request_id: null })}
                style={{ marginRight: '0.5rem' }}
                data-testid="add-order-btn"
            />
            <Button
                icon="pi pi-sync"
                aria-label="Aktualisieren"
                onClick={() => sdk.currentStore.refresh()}
                disabled={!!sdk.currentStore?.refreshTimer}
                data-testid="refresh-orders-btn"
            />
        </div>
    );

    // Define actions for requests
    const actions: MatenTableAction[] = [
        {
            icon: 'pi pi-pencil',
            tooltip: 'Antrag bearbeiten',
            tooltipPosition: 'left',
            onClick: (rowData: any) =>
                sdk.openDialog(RequestDialogStore, { request_id: rowData.id }),
            testId: 'edit-request-btn',
            className: 'p-button-info',
        },
        // {
        //     icon: 'pi pi-trash',
        //     tooltip: 'Antrag löschen',
        //     tooltipPosition: 'left',
        //     onClick: (rowData: any) => {
        //         sdk.currentStore.onDeleteRequest(rowData);
        //     },
        //     testId: 'delete-request-btn',
        //     className: 'p-button-danger'
        // }
    ];

    // Status options for filter
    const statusOptions = [
        { label: 'Ausstehend', value: 'pending' },
        { label: 'Genehmigt', value: 'approved' },
        { label: 'Abgelehnt', value: 'rejected' },
    ];

    const tableFilter: MatenFilter[] = [
        {
            title: 'Status filtern',
            property: 'filterStatus',
            removeFilter: (store: any) => {
                store.clearAllFilters();
            },
            chipContent: (filter: any) =>
                `Status: ${statusOptions.find(option => option.value === filter)?.label || ''}`,
            filterComponent: (store: any) => (
                <Dropdown
                    value={store?.filterStatus || ''}
                    options={statusOptions}
                    onChange={(e: any) => store.applyFilterStatus(e.value)}
                    placeholder="Status auswählen"
                    style={{ width: '100%' }}
                    data-testid="status-filter-dropdown"
                />
            ),
        },
        {
            title: 'Objektleiter auswählen',
            property: 'filterManager',
            removeFilter: (store: any) => store.applyFilterManager(null),
            chipContent: (filter: any) => `Objektleiter: ${filter?.name || ''}`,
            filterComponent: (store: any) => (
                <Dropdown
                    value={store?.filterManager || null}
                    options={store?.available_managers || []}
                    onChange={(e: any) => store.applyFilterManager(e.value)}
                    onShow={() => store?.fetchAvailableManagers()} // Lazy load managers when dropdown is focused
                    placeholder="Objektleiter auswählen"
                    optionLabel="name"
                    loading={false}
                    style={{ width: '100%' }}
                    data-testid="manager-filter-dropdown"
                />
            ),
        },
    ];

    return (
        <>
            <MatenTable
                data={sdk.currentStore?.allRequests || []}
                store={sdk.currentStore}
                columns={columns}
                actions={actions}
                filter={tableFilter}
                toolbarContent={headerContent}
                search={{
                    placeholder: 'Anträge durchsuchen (Name oder Typ)',
                }}
                emptyMessage="Keine Anträge gefunden"
                entity={{ singular: 'Antrag', plural: 'Anträge' }}
                testId={'requests-overview'}
                pagination={{ rows: 30 }}
            />
            <DeleteConfirmationDialog
                store={sdk.currentStore}
                onConfirm={(reason: string) => sdk.currentStore.onDeleteRequestConfirm(reason)}
                onCancel={() => sdk.currentStore.onDeleteRequestCancel()}
            />
            <ApproveConfirmationDialog
                store={sdk.currentStore}
                onConfirm={(comment: string) => sdk.currentStore.onApproveRequestConfirm(comment)}
                onCancel={() => sdk.currentStore.onApproveRequestCancel()}
            />
            <RejectConfirmationDialog
                store={sdk.currentStore}
                onConfirm={(reason: string) => sdk.currentStore.onRejectRequestConfirm(reason)}
                onCancel={() => sdk.currentStore.onRejectRequestCancel()}
            />
        </>
    );
});

export default RequestOverview;
